import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { Button, StateParams, Textfield } from '@clientbase/clientbase-library'
import { yupResolver } from '@hookform/resolvers/yup'
import { Checkbox, FormControlLabel, Grid, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { DataService } from 'api/DataService'
import { useOnboardingContext } from 'context/OnboardingContext'
import mixpanel from 'mixpanel-browser'
import { ActivateAccountFormsProps } from 'templates/ActivateAccount/ActivateAccount'

import InputController from 'components/InputController'
import StateAutocomplete from 'components/StateAutocomplete'

import { Debouncer, onlyNumber, UFOptions } from 'utils'
import { getCEPData } from 'utils/cepService'
import { normalizeDocument } from 'utils/document'
import { toastFirstError } from 'utils/yupResolverWithToast'

import { theme } from 'styles/theme'

import { AddressFormSchema, schemaAddressForm } from './'

export const initialAddressFormSchemaValue = {
  city: '',
  complement: '',
  neighborhood: '',
  number: '',
  postal_code: '',
  street: ''
} as AddressFormSchema

type OwnerAddress = {
  street?: string
  number?: string
  complement?: string
  neighborhood?: string
  city?: string
  state?: string
  postal_code?: string
  reference_point?: string
}

type AddressValues = AddressFormSchema & {
  state?: string
  owner_address?: OwnerAddress | null
}

const COMPANY_FIELDS = [
  'street',
  'number',
  'complement',
  'neighborhood',
  'city',
  'postal_code',
  'reference_point'
] as const

const ufOption = (uf?: string) =>
  UFOptions.find((option) => option.value === uf) || { label: '', value: '' }

const AddressForm = ({
  sucessSubmit,
  handleBackStep
}: ActivateAccountFormsProps) => {
  const { handleChange, values, isStone } = useOnboardingContext()
  const isCNPJ = normalizeDocument(values['1']?.document || '').length === 14
  // SC-10941: na PJ com cartão na Stone, o sócio precisa de endereço próprio — ou
  // o mesmo da empresa, que é o caso mais comum.
  const askOwnerAddress = isStone && isCNPJ
  const saved = (values['2'] || {}) as AddressValues
  const ownerSaved = saved.owner_address
  const ownerSameAsCompany =
    !ownerSaved ||
    (ownerSaved.street === saved.street &&
      ownerSaved.number === saved.number &&
      ownerSaved.postal_code === saved.postal_code)

  const [stateError, setStateError] = useState<string | undefined>()
  const [stateValue, setStateValue] = useState<StateParams | null>({
    label: '',
    value: ''
  })

  const [ownerStateError, setOwnerStateError] = useState<string | undefined>()
  const [ownerStateValue, setOwnerStateValue] = useState<StateParams | null>(
    ufOption(ownerSaved?.state)
  )

  const { handleSubmit, control, watch, setValue } = useForm<AddressFormSchema>(
    {
      defaultValues: {
        ...saved,
        reference_point: saved.reference_point || '',
        isStone,
        isCNPJ,
        owner_same_as_company: ownerSameAsCompany,
        owner_postal_code: ownerSaved?.postal_code || '',
        owner_street: ownerSaved?.street || '',
        owner_number: ownerSaved?.number || '',
        owner_complement: ownerSaved?.complement || '',
        owner_neighborhood: ownerSaved?.neighborhood || '',
        owner_city: ownerSaved?.city || '',
        owner_reference_point: ownerSaved?.reference_point || ''
      },
      resolver: yupResolver(schemaAddressForm)
    }
  )

  const { postal_code, owner_postal_code, owner_same_as_company } = watch()

  const onSubmit: SubmitHandler<AddressFormSchema> = async (data) => {
    const postal_code = onlyNumber(data?.postal_code)
    const company = Object.fromEntries(
      COMPANY_FIELDS.map((field) => [field, data[field]])
    )

    if (
      askOwnerAddress &&
      !data.owner_same_as_company &&
      !ownerStateValue?.value
    ) {
      setOwnerStateError('Estado do sócio obrigatório.')
      return sucessSubmit({
        error: true,
        message: 'Estado do sócio obrigatório.'
      })
    }

    const ownerPayload = !askOwnerAddress
      ? {}
      : data.owner_same_as_company
      ? { owner_address_same_as_company: true }
      : {
          owner_address: {
            street: data.owner_street,
            number: data.owner_number,
            complement: data.owner_complement,
            neighborhood: data.owner_neighborhood,
            city: data.owner_city,
            postal_code: onlyNumber(data.owner_postal_code || ''),
            reference_point: data.owner_reference_point,
            state: ownerStateValue?.value.trim()
          }
        }

    const response = await DataService({
      type: 'PATCH',
      data: {
        addresses_attributes: [
          { ...company, postal_code, state: stateValue?.value.trim() }
        ],
        ...ownerPayload
      },
      url: '/v1/onboarding/address'
    })

    if (!response.error) {
      const companyAddress = {
        ...company,
        postal_code,
        state: stateValue?.value
      }
      const ownerAddress = !askOwnerAddress
        ? ownerSaved
        : data.owner_same_as_company
        ? companyAddress
        : (ownerPayload as { owner_address: OwnerAddress }).owner_address
      handleChange(2, {
        ...data,
        state: stateValue?.value,
        owner_address: ownerAddress
      } as AddressFormSchema)
    }
    return sucessSubmit({ error: response?.error, message: response?.message })
  }

  const getCEP = async (cep: string) => {
    try {
      const response = await getCEPData(cep)
      if (response.error) {
        return
      }

      const { localidade, logradouro, bairro, uf } = response

      !!localidade && setValue('city', localidade)
      !!bairro && setValue('neighborhood', bairro)
      !!logradouro && setValue('street', logradouro)
      setStateValue(UFOptions.filter((cepItem) => cepItem.value === uf)[0])
    } catch (err) {
      return
    }
  }
  const awaitForSubmitCEP = Debouncer(getCEP, 1000)

  const getOwnerCEP = async (cep: string) => {
    try {
      const response = await getCEPData(cep)
      if (response.error) return

      const { localidade, logradouro, bairro, uf } = response
      !!localidade && setValue('owner_city', localidade)
      !!bairro && setValue('owner_neighborhood', bairro)
      !!logradouro && setValue('owner_street', logradouro)
      setOwnerStateValue(ufOption(uf))
    } catch (err) {
      return
    }
  }
  const awaitForOwnerCEP = Debouncer(getOwnerCEP, 1000)

  useEffect(() => {
    if (onlyNumber(postal_code).length === 8) {
      awaitForSubmitCEP(onlyNumber(postal_code))
    }
  }, [postal_code])

  useEffect(() => {
    if (onlyNumber(owner_postal_code || '').length === 8) {
      awaitForOwnerCEP(onlyNumber(owner_postal_code || ''))
    }
  }, [owner_postal_code])
  const mixpanelTyped = mixpanel as unknown as { __loaded: string }
  return (
    <form onSubmit={handleSubmit(onSubmit, toastFirstError)}>
      <Box mb="1.5rem">
        <Typography
          sx={{
            [theme.breakpoints.down(800)]: {
              fontSize: '18px'
            }
          }}
          variant="lgBold"
        >
          Dados de endereço
        </Typography>
      </Box>
      <Box display="flex" flexDirection="column" gap="15px">
        <InputController control={control} name="postal_code">
          <Textfield placeholder="00.000-000" label="CEP" mask="cep" />
        </InputController>
        <InputController
          placeholder="Endereço - Rua"
          control={control}
          name="street"
        >
          <Textfield label="Logradouro" />
        </InputController>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={6}>
            <InputController control={control} name="number">
              <Textfield placeholder="Número" label="Número" />
            </InputController>
          </Grid>
          <Grid item xs={12} lg={6}>
            <InputController
              placeholder="Casa/Apartamento/Comercial"
              control={control}
              name="complement"
            >
              <Textfield label="Complemento" />
            </InputController>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={4}>
            <InputController control={control} name="neighborhood">
              <Textfield placeholder="Bairro" label="Bairro" />
            </InputController>
          </Grid>
          <Grid item xs={12} lg={4}>
            <InputController control={control} name="city">
              <Textfield placeholder="Cidade" label="Cidade" />
            </InputController>
          </Grid>
          <Grid item xs={12} lg={4}>
            <StateAutocomplete
              error={stateError}
              setStateError={setStateError}
              setStateValue={setStateValue}
              stateValue={stateValue}
            />
          </Grid>
        </Grid>
        {isStone && (
          <InputController
            placeholder="Ex.: Ao lado da padaria"
            control={control}
            name="reference_point"
          >
            <Textfield label="Ponto de referência" />
          </InputController>
        )}
      </Box>

      {askOwnerAddress && (
        <Box mt="2rem" display="flex" flexDirection="column" gap="15px">
          <Typography variant="mdBold">Endereço do sócio</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!owner_same_as_company}
                onChange={(event) =>
                  setValue('owner_same_as_company', event.target.checked)
                }
              />
            }
            label={
              <Typography variant="smLight">
                O sócio mora no mesmo endereço da empresa
              </Typography>
            }
          />
          {!owner_same_as_company && (
            <>
              <InputController control={control} name="owner_postal_code">
                <Textfield placeholder="00.000-000" label="CEP" mask="cep" />
              </InputController>
              <InputController control={control} name="owner_street">
                <Textfield label="Logradouro" />
              </InputController>
              <Grid container spacing={2}>
                <Grid item xs={12} lg={6}>
                  <InputController control={control} name="owner_number">
                    <Textfield label="Número" />
                  </InputController>
                </Grid>
                <Grid item xs={12} lg={6}>
                  <InputController control={control} name="owner_complement">
                    <Textfield label="Complemento" />
                  </InputController>
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} lg={4}>
                  <InputController control={control} name="owner_neighborhood">
                    <Textfield label="Bairro" />
                  </InputController>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <InputController control={control} name="owner_city">
                    <Textfield label="Cidade" />
                  </InputController>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <StateAutocomplete
                    error={ownerStateError}
                    setStateError={setOwnerStateError}
                    setStateValue={setOwnerStateValue}
                    stateValue={ownerStateValue}
                  />
                </Grid>
              </Grid>
              <InputController control={control} name="owner_reference_point">
                <Textfield label="Ponto de referência" />
              </InputController>
            </>
          )}
        </Box>
      )}

      <Box mt={'3.5rem'} display="flex" gap={1.5}>
        <Button
          sx={{
            backgroundColor: 'white'
          }}
          fullWidth
          variantButton="outlined"
          type="button"
          onClick={handleBackStep}
        >
          Voltar
        </Button>
        <Box
          width="100%"
          onClick={() =>
            mixpanelTyped.__loaded &&
            mixpanel.track('Preencheu endereço concluir cadastro')
          }
        >
          <Button fullWidth variantButton="primary" type="submit">
            Continuar
          </Button>
        </Box>
      </Box>
    </form>
  )
}

export default AddressForm
